import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useActionPlanEligibleScores } from "src/hooks/useActionPlanEligibleScores";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { Post } from "src/api/client-api/post/postSchema";
import { useUpsertActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useUpsertActionPlan";
import { getAllActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { getOneActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { getAllActionPlanActionsKey } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import {
    findActionPlanForPost,
    reconcileActionPlanCreation,
    ActionPlanAlreadyExistsError,
    useActionPlanEstimate,
    useActionPlanInvitation,
    useAdoptTemplatePlan,
} from "src/api/client-api/ActionPlan/useActionPlanInvitation";
import {
    classifyTemplateUseFailure,
    isCreationOutcomeUncertain,
} from "src/api/client-api/ActionPlan/templateUseFailure";
import { ScoreService } from "src/services/ScoreService";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";
import { ActionPlanInvitationDialog } from "./ActionPlanInvitationDialog";
import type { InvitationNotice } from "./TemplateUseAlerts";

type Props = {
    post: Pick<Post, "id" | "ssqId" | "campaign">;
    ssqTitle?: string;
    onClose: () => void;
};

const getProjection = (
    estimate: {
        isError: boolean;
        isFetching?: boolean;
        error?: unknown;
        data?:
            | { allActionsDropped: true; values?: null; templateVersion: number }
            | { allActionsDropped: false; values: Record<string, number | null>; templateVersion: number };
    },
    target: { id: number } | undefined,
) => {
    if (estimate.isError) {
        if (estimate.error instanceof ActionPlanAlreadyExistsError) {
            return { status: "conflict" as const };
        }

        const failure = classifyTemplateUseFailure(estimate.error);

        return {
            status: "error" as const,
            ...(failure.kind === "throttled" || failure.kind === "outage" ? { reason: failure.kind } : {}),
        };
    }

    if (!target) {
        return { status: "error" as const };
    }

    if (estimate.data && !estimate.isFetching) {
        if (estimate.data.allActionsDropped) {
            return { status: "all-dropped" as const };
        }

        return { status: "success" as const, values: estimate.data.values };
    }

    return { status: "loading" as const };
};

export const CompanyActionPlanInvitation = (props: Props) => {
    const { post, ssqTitle, onClose } = props;
    const { company, ssqVersion } = useCompanyMetadata();
    const { entitySsq } = useEntityScores();
    const { user, campaigns } = useFoodpilotContext();
    const { applicationParameters, isActionPlanSimplified } = useApplicationParameters();
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const client = useQueryClient();
    const navigate = useNavigate();
    const create = useUpsertActionPlan();
    const adoption = useAdoptTemplatePlan();
    const [isFinishingCreation, setIsFinishingCreation] = useState(false);
    const [isVerifyingCreation, setIsVerifyingCreation] = useState(false);
    const [creationUnconfirmed, setCreationUnconfirmed] = useState(false);
    const [lastAttempt, setLastAttempt] = useState<"adopt" | "create-own">();
    const [notice, setNotice] = useState<InvitationNotice>();
    const creationInFlight = useRef(false);
    const verificationController = useRef<AbortController | null>(null);
    const simplified = isActionPlanSimplified();
    const target = campaigns.find(
        (campaign) => campaign["@id"] === applicationParameters?.["actionPlan-targetCampaign"],
    );
    const invitation = useActionPlanInvitation(post.id, !!target, {
        onSuccess: (data) => {
            if (!data.eligible) {
                onClose();
            }
        },
        onError: () => {
            onClose();
            snackbar.setSnackbarProps({
                type: "error",
                message: t("action_plan.invitation.load_failed", "Unable to load the proposed plan. Please try again."),
            });
        },
    });
    const templateId = invitation.data?.templateId;
    const ssqId = post.ssqId ?? ssqVersion?.ssq.id;
    const scoreIds = entitySsq ? ScoreService.getScoreIdsFromSsq(entitySsq, []) : [];
    const scores = useActionPlanEligibleScores().filter((score) => scoreIds.includes(score.id));
    const estimate = useActionPlanEstimate(
        post.id,
        templateId,
        target?.id,
        invitation.data?.eligible === true && !!templateId && !!target,
    );

    const templateVersion = estimate.isFetching || estimate.isError ? undefined : estimate.data?.templateVersion;
    const proposalUnavailable =
        notice === "unavailable" || classifyTemplateUseFailure(estimate.error).kind === "unavailable";

    useEffect(
        () => () => {
            const controller = verificationController.current;
            verificationController.current = null;
            controller?.abort();
        },
        [post.id],
    );

    const onCreationSuccess = async (controller: AbortController) => {
        if (controller.signal.aborted) return;

        setIsFinishingCreation(true);
        try {
            await Promise.allSettled([
                client.invalidateQueries([getAllActionPlanKey]),
                client.invalidateQueries([getOneActionPlanKey]),
                client.invalidateQueries([getAllActionPlanActionsKey]),
            ]);

            if (controller.signal.aborted) return;

            onClose();

            if (ssqId) {
                navigate(URLHandler.company.actions.planning(company.id, ssqId));
            }
        } finally {
            if (!controller.signal.aborted) {
                setIsFinishingCreation(false);
            }
        }
    };

    const verifyUncertainCreation = async (error: unknown, startedAt: number, controller: AbortController) => {
        if (controller.signal.aborted || !isCreationOutcomeUncertain(error)) {
            return;
        }

        setIsVerifyingCreation(true);
        try {
            const result = await reconcileActionPlanCreation(post.id, startedAt, controller.signal);

            if (result.status === "created") {
                await onCreationSuccess(controller);
            } else if (result.status === "unconfirmed" && !controller.signal.aborted) {
                setCreationUnconfirmed(true);
            }
        } finally {
            if (verificationController.current === controller) {
                verificationController.current = null;
                setIsVerifyingCreation(false);
            }
        }
    };

    const handleCheckCreation = () => {
        if (creationInFlight.current) return;

        const controller = new AbortController();
        verificationController.current?.abort();
        verificationController.current = controller;
        creationInFlight.current = true;
        setIsVerifyingCreation(true);

        void findActionPlanForPost(post.id, controller.signal)
            .then(async (planId) => {
                if (planId) {
                    await onCreationSuccess(controller);
                } else if (!controller.signal.aborted) {
                    setCreationUnconfirmed(false);
                }
            })
            .catch(() => undefined)
            .finally(() => {
                creationInFlight.current = false;
                if (verificationController.current === controller) {
                    verificationController.current = null;
                    setIsVerifyingCreation(false);
                }
            });
    };

    const handleCreateOwn = () => {
        if (!target || !ssqId || creationInFlight.current) return;

        creationInFlight.current = true;
        const startedAt = Date.now();
        const controller = new AbortController();
        verificationController.current?.abort();
        verificationController.current = controller;
        setLastAttempt("create-own");
        setNotice(undefined);
        setCreationUnconfirmed(false);

        void create
            .mutateAsync({
                payload: {
                    name: t("action_plan.trajectory.form.name", "Name"),
                    description: null,
                    simulationVaryPercentage: null,
                    startSsqPost: `/api/ssq-posts/${post.id}`,
                    endCampaign: target["@id"] ?? `/api/campaigns/${target.id}`,
                    actionPlanScoreTargets: [],
                },
            })
            .then(() => onCreationSuccess(controller))
            .catch((error) => verifyUncertainCreation(error, startedAt, controller))
            .finally(() => {
                creationInFlight.current = false;
                if (verificationController.current === controller) {
                    verificationController.current = null;
                    controller.abort();
                }
            });
    };

    const handleAdoptionFailure = async (error: unknown, startedAt: number, controller: AbortController) => {
        if (controller.signal.aborted) return;

        const failure = classifyTemplateUseFailure(error);

        switch (failure.kind) {
            case "plan_already_exists":
                await onCreationSuccess(controller);
                return;
            case "template_changed":
                setNotice("template_changed");
                await estimate.refetch();
                return;
            case "unavailable":
            case "target_year_not_after_diagnostic":
                setNotice(failure.kind);
                return;
            default:
                await verifyUncertainCreation(error, startedAt, controller);
        }
    };

    const handleAdopt = () => {
        if (!target || !ssqId || !templateId || templateVersion === undefined || creationInFlight.current) return;

        creationInFlight.current = true;
        const startedAt = Date.now();
        const controller = new AbortController();
        verificationController.current?.abort();
        verificationController.current = controller;
        setLastAttempt("adopt");
        setNotice(undefined);
        setCreationUnconfirmed(false);
        void adoption
            .mutateAsync({
                postId: post.id,
                input: {
                    templateId,
                    endCampaignId: target.id,
                    templateVersion,
                    name: t("action_plan.invitation.default_plan_name", "Action plan"),
                },
            })
            .then(() => onCreationSuccess(controller))
            .catch((error) => handleAdoptionFailure(error, startedAt, controller))
            .finally(() => {
                creationInFlight.current = false;
                if (verificationController.current === controller) {
                    verificationController.current = null;
                    controller.abort();
                }
            });
    };

    const busy = create.isLoading || adoption.isLoading || isFinishingCreation || isVerifyingCreation;
    const failed =
        !busy && !creationUnconfirmed && (lastAttempt === "adopt" ? adoption.isError : Boolean(create.isError));
    const projection = getProjection(estimate, target);

    if (!invitation.data?.eligible) return null;

    return (
        <ActionPlanInvitationDialog
            firstName={user.trustedUser.firstname}
            ssqTitle={ssqTitle ?? ssqVersion?.ssq.title ?? ""}
            campaign={post.campaign?.label ?? ""}
            proposed={!!templateId && !proposalUnavailable}
            targetYear={target?.year}
            simplified={simplified}
            scores={scores}
            projection={projection}
            busy={busy}
            creationType={lastAttempt}
            failed={failed}
            creationUnconfirmed={creationUnconfirmed}
            retryAdoption={failed && lastAttempt === "adopt" && !notice}
            adoptDisabled={templateVersion === undefined}
            notice={notice}
            onRetryEstimate={projection.status === "error" ? () => void estimate.refetch() : undefined}
            onClose={onClose}
            onCheckCreation={handleCheckCreation}
            onCreateOwn={handleCreateOwn}
            onAdopt={handleAdopt}
        />
    );
};
